import type * as React from "react";

import { toneFill, toneInk } from "./Chip";
import { CalendarIcon, ClockIcon } from "./Icons";
import { formatClockTime } from "./TimeLabel";
import { type HistoryItemKind, TypeChip } from "./TypeChip";
import { cn } from "../lib/cn";

const kindTone = {
  invoice: "recorded",
  visit: "brian",
  draft: "attention",
  lab: "attention",
  message: "brian",
  document: "brian",
} as const;

const kindWord: Record<HistoryItemKind, string> = {
  invoice: "Invoice",
  visit: "Visit",
  draft: "Visit · draft note",
  document: "Document",
  lab: "Lab",
  message: "Message",
};

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const dayFormat = new Intl.DateTimeFormat("en", {
  weekday: "short",
  month: "short",
  day: "numeric",
});

export interface HistoryRailProps extends React.OlHTMLAttributes<HTMLOListElement> {
  children: React.ReactNode;
  ref?: React.Ref<HTMLOListElement>;
}

export function HistoryRail({
  children,
  className,
  ...rest
}: HistoryRailProps) {
  return (
    <ol
      className={cn(
        "relative ml-[30px] flex flex-col gap-0.5 border-l-[1.5px] border-rule-strong pl-3.5",
        className,
      )}
      {...rest}
    >
      {children}
    </ol>
  );
}

export interface HistoryItemProps extends Omit<
  React.LiHTMLAttributes<HTMLLIElement>,
  "children" | "onSelect" | "title"
> {
  kind: HistoryItemKind;
  date: Date | string;
  dateLabel?: string;
  typeLabel?: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  selected?: boolean;
  onSelect?: React.MouseEventHandler<HTMLButtonElement>;
  label?: string;
  ref?: React.Ref<HTMLLIElement>;
}

const PADDING = "px-3 py-2.5";

export function HistoryItem({
  kind,
  date,
  dateLabel,
  typeLabel,
  title,
  children,
  selected = false,
  onSelect,
  label,
  className,
  ...rest
}: HistoryItemProps) {
  const when = date instanceof Date ? date : new Date(date);
  const valid = !Number.isNaN(when.getTime());
  const today = valid && sameDay(when, new Date());
  const shownDate =
    dateLabel ??
    (valid
      ? today
        ? `Today · ${formatClockTime(when)}`
        : dayFormat.format(when).replace(",", "")
      : String(date));

  const face = (
    <>
      <span className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1">
        <time
          dateTime={valid ? when.toISOString() : undefined}
          className="inline-flex items-center whitespace-nowrap text-mono-time text-fine-print text-ink-body"
        >
          {today ? (
            <ClockIcon className="mr-[0.35em]" />
          ) : (
            <CalendarIcon className="mr-[0.35em]" />
          )}
          {shownDate}
        </time>
        <TypeChip kind={kind} size="sm">
          {typeLabel ?? kindWord[kind]}
        </TypeChip>
      </span>
      <span className="mt-[3px] block font-body text-sm font-semibold leading-[1.3] text-ink">
        {title}
      </span>
      {children != null && (
        <span className="mt-0.5 block font-body text-xs leading-[1.35] text-annotation">
          {children}
        </span>
      )}
    </>
  );

  return (
    <li
      aria-current={selected ? "true" : undefined}
      className={cn(
        "relative rounded-md border",
        selected ? "border-accent bg-tint-accent" : "border-transparent",
        onSelect ? "transition duration-150 ease-paper" : PADDING,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute -left-[20.75px] top-4 size-2.5 rounded-full border-[1.5px]",
          selected
            ? "border-accent bg-accent"
            : cn(toneFill[kindTone[kind]], toneInk[kindTone[kind]]),
        )}
      />
      {onSelect ? (
        <button
          type="button"
          onClick={onSelect}
          aria-label={label}
          className={cn(
            "flex w-full cursor-pointer flex-col rounded-md text-left",
            PADDING,
            "hover:bg-smudge focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent",
            selected && "hover:bg-transparent",
          )}
        >
          {face}
        </button>
      ) : (
        face
      )}
    </li>
  );
}
